/**
 * PORTA DE PLANILHA (Bloco 04) — forma neutra de uma aba já normalizada.
 *
 * O domínio raciocina sobre cabeçalho e linhas; ele não sabe o que é ZIP,
 * XML ou .xlsx. Quem sabe disso é `src/infrastructure/import/xlsx-reader.ts`,
 * que produz estas estruturas. Trocar o formato de entrada (XLSX, CSV, outro)
 * não toca em nenhuma regra de importação.
 */

export interface SpreadsheetRow {
  /** Número da linha na planilha (1-based), para a mensagem de erro apontar o lugar certo. */
  readonly rowNumber: number;
  readonly values: Readonly<Record<string, string>>;
}

export interface SpreadsheetTable {
  readonly sheetName: string;
  /** Rótulos do cabeçalho, na ordem das colunas. */
  readonly headers: readonly string[];
  /** Linha do cabeçalho na planilha (1-based). */
  readonly headerRowNumber: number;
  readonly rows: readonly SpreadsheetRow[];
}

export interface SpreadsheetWorkbook {
  readonly sheetNames: readonly string[];
  /** Devolve a aba normalizada, ou null se ela não existir. */
  table(sheetName: string): SpreadsheetTable | null;
}

/** Localiza uma aba ignorando caixa e acentuação — o usuário renomeia abas na prática. */
/**
 * TODAS as abas cujo nome CONTÉM um dos termos, na ordem do arquivo.
 *
 * `findSheetName` casa nome exato e devolve uma só. A planilha cadastral real
 * do cliente traz a tubulação em DUAS tabelas — "Dados de processo e
 * identificação" e "Materiais, dimensões, normas e rastreabilidade" — que,
 * exportadas, viram abas como "Tubulações - Processo" e
 * "Tubulações - Materiais". Com a busca exata, nenhuma das duas era
 * encontrada; e mesmo que uma fosse, a outra seria silenciosamente ignorada e
 * metade do cadastro técnico se perderia sem aviso.
 */
export function findSheetNames(workbook: SpreadsheetWorkbook, ...candidates: readonly string[]): string[] {
  const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();
  const wanted = candidates.map(normalize);
  return workbook.sheetNames.filter(name => {
    const normalized = normalize(name);
    return wanted.some(term => normalized === term || normalized.includes(term));
  });
}

export function findSheetName(workbook: SpreadsheetWorkbook, ...candidates: readonly string[]): string | null {
  const normalize = (value: string) => value.normalize("NFD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();
  for (const candidate of candidates) {
    const wanted = normalize(candidate);
    const found = workbook.sheetNames.find(name => normalize(name) === wanted);
    if (found) return found;
  }
  return null;
}
